import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { usePurchaseOrderLineItem } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import { ListChecksIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { T, Var, useGT } from 'gt-react';

interface PurchaseOrderLineItemPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

export function PurchaseOrderLineItemPreview({
  entityId,
  className,
}: PurchaseOrderLineItemPreviewProps) {
  const gt = useGT();
  const { lineItem, purchaseOrder, isLoading, error } =
    usePurchaseOrderLineItem(entityId);

  const formatCurrencyValue = (
    value: string | undefined | null,
    currency = 'USD',
  ): string => {
    if (!value) return gt('N/A', { context: 'not applicable abbreviation' });

    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;

    return numeric.toLocaleString(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    });
  };

  if (!entityId) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ListChecksIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="PO line item preview title">PO Line Item</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="missing entity id for preview">No entity ID provided</T>
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ListChecksIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="PO line item preview title">PO Line Item</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="loading state">Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !lineItem || !purchaseOrder) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ListChecksIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="PO line item preview title">PO Line Item</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="error loading PO line item">
            Unable to load line item details
          </T>
          {error && (
            <span className="block mt-1">
              <T>
                Error: <Var>{String(error)}</Var>
              </T>
            </span>
          )}
        </p>
      </div>
    );
  }

  const currency = purchaseOrder.currency || 'USD';

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <ListChecksIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">
          <T>
            Line <Var>{lineItem.lineNumber}</Var>
          </T>
        </h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div>
          <span className="text-muted-foreground">
            <T context="PO reference label">PO: </T>
          </span>
          <span className="font-medium">
            {purchaseOrder.externalId || (
              <T>
                PO-<Var>{purchaseOrder.internalId}</Var>
              </T>
            )}
          </span>
        </div>

        {lineItem.description && (
          <div>
            <span className="text-muted-foreground">
              <T context="line item field label">Description: </T>
            </span>
            <span className="font-medium">{lineItem.description}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {lineItem.quantity !== null && lineItem.quantity !== undefined && (
            <div>
              <span className="text-muted-foreground">
                <T context="line item field label">Quantity: </T>
              </span>
              <span className="font-medium">
                {lineItem.uom ? (
                  <T>
                    <Var>{lineItem.quantity}</Var> <Var>{lineItem.uom}</Var>
                  </T>
                ) : (
                  <Var>{lineItem.quantity}</Var>
                )}
              </span>
            </div>
          )}

          {lineItem.unitPrice && (
            <div>
              <span className="text-muted-foreground">
                <T context="line item field label">Unit Price: </T>
              </span>
              <span className="font-medium">
                {formatCurrencyValue(lineItem.unitPrice, currency)}
              </span>
            </div>
          )}
        </div>

        {lineItem.totalPrice && (
          <div>
            <span className="text-muted-foreground">
              <T context="line item field label">Total: </T>
            </span>
            <span className="font-medium">
              {formatCurrencyValue(lineItem.totalPrice, currency)}
            </span>
          </div>
        )}

        {lineItem.expectedDeliveryDate && (
          <div>
            <span className="text-muted-foreground">
              <T context="line item field label">Expected Delivery: </T>
            </span>
            <span className="font-medium">
              {formatDateOnlyString(lineItem.expectedDeliveryDate)}
            </span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={`/accounts-payable/purchase-orders/${purchaseOrder.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          <T context="link to purchase order from line item">
            View Purchase Order
          </T>
        </a>
      </div>
    </div>
  );
}
